import { Controller } from "@hotwired/stimulus"
import { paintSignPoster, SIGN_POSTER } from "../gallery/sign_poster"

type Three = typeof import("three/webgpu")

// The exhibition sign of the 3D gallery's forecourt, previewed beside the exhibition form of
// the workspace: the same stone stele and the same poster (gallery/sign_poster), repainted as
// the title is typed. three.js is loaded on connect, in its own chunk: until then, and without
// WebGPU nor WebGL, the poster alone is shown, flat.
export default class extends Controller {
  static targets = ["title", "stage"]
  static values  = { kicker: String, details: String, posterUrl: String }

  declare readonly titleTarget: HTMLInputElement
  declare readonly stageTarget: HTMLElement
  declare readonly kickerValue: string
  declare readonly detailsValue: string
  declare readonly posterUrlValue: string

  private _poster = document.createElement("canvas")
  private _three?: Three
  private _renderer?: InstanceType<Three["WebGPURenderer"]>
  private _scene?: InstanceType<Three["Scene"]>
  private _camera?: InstanceType<Three["PerspectiveCamera"]>
  private _texture?: InstanceType<Three["CanvasTexture"]>
  private _resize?: ResizeObserver
  private _frame = 0
  private _disconnected = false

  async connect() {
    this._disconnected = false
    this._paint()
    this._showFlatPoster()   // while three.js loads, and for good without WebGPU nor WebGL
    document.fonts?.ready.then(() => this.update())

    try {
      this._three = await import("three/webgpu")
      if (this._disconnected) return
      await this._build()
    } catch {
      // The flat poster stays
    }
  }

  disconnect() {
    this._disconnected = true
    cancelAnimationFrame(this._frame)
    this._resize?.disconnect()
    this._renderer?.dispose()
  }

  // On every keystroke in the title: one repaint per frame at most
  update() {
    cancelAnimationFrame(this._frame)
    this._frame = requestAnimationFrame(() => {
      this._paint()
      if (this._texture) this._texture.needsUpdate = true
      this._render()
    })
  }

  _paint() {
    paintSignPoster(this._poster, {
      kicker: this.kickerValue,
      title: this.titleTarget.value.trim(),
      details: this.detailsValue || null
    })
  }

  // The stele of galerie_controller#_buildEnseigne: 1 m × 1.95 m, the poster top-aligned on its
  // face, seen at three-quarters on the panel's background, with its shadow on the ground
  async _build() {
    const THREE = this._three!
    const canvas = document.createElement("canvas")
    const renderer = new THREE.WebGPURenderer({ canvas, antialias: true, alpha: true })
    await renderer.init()
    if (this._disconnected) { renderer.dispose(); return }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this._renderer = renderer

    const scene = new THREE.Scene()
    const W = 1.0, H = 1.95, D = 0.14
    const stele = new THREE.Group()
    stele.rotation.y = -0.45
    const slab = new THREE.Mesh(new THREE.BoxGeometry(W, H, D), new THREE.MeshStandardMaterial({ color: 0xa89880, roughness: 0.94 }))
    slab.position.y = H / 2
    slab.castShadow = true
    stele.add(slab)

    const hang = (texture: InstanceType<Three["Texture"]>, aspect: number) => {
      texture.colorSpace = THREE.SRGBColorSpace
      texture.anisotropy = renderer.getMaxAnisotropy()
      // Unlit: the paper keeps the colours of tokens.css, however the stone is lit
      const h = Math.min(1.25, 0.8 / aspect), w = h * aspect
      const poster = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: texture }))
      poster.position.set(0, H - 0.12 - h / 2, D / 2 + 0.002)
      stele.add(poster)
      this._render()
    }
    if (this.posterUrlValue) {
      new THREE.TextureLoader().load(this.posterUrlValue, (texture) => {
        const image = texture.image as HTMLImageElement
        hang(texture, image.width / image.height)
      })
    } else {
      this._texture = new THREE.CanvasTexture(this._poster)
      hang(this._texture, SIGN_POSTER.width / SIGN_POSTER.height)
    }

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(8, 8), new THREE.ShadowMaterial({ opacity: 0.1 }))
    ground.rotation.x = -Math.PI / 2
    ground.receiveShadow = true

    // Bright daylight, as in the forecourt, and a high sun from the front left: a short shadow behind
    const sky = new THREE.HemisphereLight(0xffffff, 0xe8e0d4, 2.8)
    const sun = new THREE.DirectionalLight(0xfff8ee, 2.4)
    sun.position.set(-1.5, 6, 4)
    sun.castShadow = true
    sun.shadow.mapSize.set(1024, 1024)
    sun.shadow.radius = 6
    Object.assign(sun.shadow.camera, { left: -2, right: 2, top: 3, bottom: -1 })
    scene.add(stele, ground, sky, sun)
    this._scene = scene

    this._camera = new THREE.PerspectiveCamera(28, 1, 0.1, 50)
    this._camera.position.set(0.6, 1.2, 5.6)
    this._camera.lookAt(0.25, 0.95, 0)

    this.stageTarget.replaceChildren(canvas)
    this._resize = new ResizeObserver(() => this._fit())
    this._resize.observe(this.stageTarget)
    this._fit()
  }

  _fit() {
    if (!this._renderer || !this._camera) return
    const { clientWidth: width, clientHeight: height } = this.stageTarget
    if (!width || !height) return
    this._renderer.setSize(width, height, false)
    this._camera.aspect = width / height
    this._camera.updateProjectionMatrix()
    this._render()
  }

  _render() {
    if (this._renderer && this._scene && this._camera) this._renderer.render(this._scene, this._camera)
  }

  _showFlatPoster() {
    this._poster.classList.add("sign-preview-flat")
    this.stageTarget.replaceChildren(this._poster)
  }
}
